import {
  BOARD_SIZE,
  planExtraction,
  type ExtractionPlan,
  type Position,
  type Vector,
  type VisualCell,
  type VisualInventory,
} from "./extractionPlanner";

/**
 * A removal target supplied by the resolved game transition. The scheduler deliberately
 * does not determine which fields are removed; it only serializes their presentation.
 */
export type ExtractionTarget = {
  readonly position: Position;
  readonly departureVector?: Vector;
};

export type ExtractionSequenceRequest = {
  /** The visual board immediately before the resolved logical removal. */
  readonly inventory: VisualInventory;
  /** The ordinary emptied source field. It is always presented before cleanup fields. */
  readonly source: ExtractionTarget;
  /**
   * The one logical orphan wave. Input order is intentionally ignored so engine set
   * iteration cannot affect recordings or restoration.
   */
  readonly orphans?: readonly ExtractionTarget[];
};

export type ExtractionPhase = "source" | "orphan";

export type ExtractionSequenceStep = {
  readonly phase: ExtractionPhase;
  readonly target: Position;
  readonly departureVector?: Vector;
  /** Immutable visual snapshot immediately before this extraction. */
  readonly before: VisualInventory;
  /** Immutable clone of the deterministic physical extraction plan. */
  readonly plan: ExtractionPlan;
  /**
   * Immutable canonical visual snapshot after this one slab has left. A push may
   * temporarily move anonymous overlay slabs, but it never changes logical field
   * positions: the target is the only new gap in this resolved snapshot.
   */
  readonly after: VisualInventory;
};

export type ExtractionSequence = {
  readonly initial: VisualInventory;
  readonly steps: readonly ExtractionSequenceStep[];
  readonly final: VisualInventory;
};

/**
 * Plans a cancellation-neutral, serial extraction presentation. Every returned
 * snapshot is a self-contained immutable value, allowing a view to discard an
 * in-flight sequence and reveal `final` without replaying partial animation.
 */
export function planExtractionSequence(request: ExtractionSequenceRequest): ExtractionSequence;
export function planExtractionSequence(request: unknown): ExtractionSequence {
  const { inventory, source, orphans } = validateSequenceRequest(request);
  const initial = snapshotInventory(inventory);
  const orderedOrphans = [...orphans].sort(compareTargets);
  const targets = [
    { phase: "source" as const, target: source },
    ...orderedOrphans.map((target) => ({ phase: "orphan" as const, target })),
  ];

  assertUniqueTargets(targets.map(({ target }) => target));

  let current = initial;
  const steps: ExtractionSequenceStep[] = [];

  for (const { phase, target } of targets) {
    const plannerRequest = makePlannerRequest(current, target);
    const plan = snapshotPlan(planExtraction(plannerRequest));
    const after = removeTargetCanonically(current, target.position);
    const step = {
      phase,
      target: snapshotPosition(target.position),
      ...(target.departureVector === undefined
        ? {}
        : { departureVector: snapshotVector(target.departureVector) }),
      before: current,
      plan,
      after,
    } satisfies ExtractionSequenceStep;

    steps.push(Object.freeze(step));
    current = after;
  }

  return Object.freeze({
    initial,
    steps: Object.freeze(steps),
    final: current,
  });
}

function validateSequenceRequest(request: unknown): {
  readonly inventory: VisualInventory;
  readonly source: ExtractionTarget;
  readonly orphans: readonly ExtractionTarget[];
} {
  if (!isRecord(request)) {
    throw new TypeError("Extraction sequence request must be an object.");
  }

  const inventory = request["inventory"];
  const source = request["source"];
  const rawOrphans = request["orphans"];

  if (!isRecord(source)) {
    throw new TypeError("Extraction sequence source must be an extraction target.");
  }
  if (rawOrphans !== undefined && !Array.isArray(rawOrphans)) {
    throw new TypeError("Extraction sequence orphans must be an array when provided.");
  }

  const normalizedSource = snapshotTarget(source);
  const orphanValues: readonly unknown[] = rawOrphans ?? [];
  const normalizedOrphans: ExtractionTarget[] = [];
  for (let index = 0; index < orphanValues.length; index += 1) {
    if (!(index in orphanValues)) {
      throw new TypeError(`Extraction sequence orphan target ${index} is missing.`);
    }
    normalizedOrphans.push(snapshotTarget(orphanValues[index]));
  }

  // The established planner is the single validator for board shape, coordinates,
  // vectors, target existence, and impossible push intermediates.
  planExtraction(makePlannerRequest(inventory as VisualInventory, normalizedSource));
  for (const orphan of normalizedOrphans) {
    planExtraction(makePlannerRequest(inventory as VisualInventory, orphan));
  }

  return {
    inventory: inventory as VisualInventory,
    source: normalizedSource,
    orphans: Object.freeze(normalizedOrphans),
  };
}

function snapshotTarget(value: unknown): ExtractionTarget {
  if (!isRecord(value)) {
    throw new TypeError("Extraction target must be an object.");
  }

  const position = value["position"];
  const departureVector = value["departureVector"];
  if (!isRecord(position)) {
    throw new TypeError("Extraction target position must be an object.");
  }
  if (departureVector !== undefined && !isRecord(departureVector)) {
    throw new TypeError("Extraction target departure vector must be an object when provided.");
  }

  const target = {
    position: snapshotPosition(position as Position),
    ...(departureVector === undefined
      ? {}
      : { departureVector: snapshotVector(departureVector as Vector) }),
  } satisfies ExtractionTarget;

  return Object.freeze(target);
}

function makePlannerRequest(
  inventory: VisualInventory,
  target: ExtractionTarget,
): {
  readonly inventory: VisualInventory;
  readonly target: Position;
  readonly departureVector?: Vector;
} {
  return target.departureVector === undefined
    ? { inventory, target: target.position }
    : { inventory, target: target.position, departureVector: target.departureVector };
}

function assertUniqueTargets(targets: readonly ExtractionTarget[]): void {
  const seen = new Set<string>();
  for (const target of targets) {
    const key = positionKey(target.position);
    if (seen.has(key)) {
      throw new RangeError(`Extraction sequence contains duplicate target ${key}.`);
    }
    seen.add(key);
  }
}

function compareTargets(left: ExtractionTarget, right: ExtractionTarget): number {
  return left.position.row - right.position.row || left.position.column - right.position.column;
}

function removeTargetCanonically(inventory: VisualInventory, target: Position): VisualInventory {
  return snapshotInventory(
    inventory.map((row, rowIndex) =>
      row.map((cell, columnIndex) =>
        rowIndex === target.row && columnIndex === target.column ? { kind: "gap" } : cell,
      ),
    ),
  );
}

function snapshotInventory(inventory: VisualInventory): VisualInventory {
  const rows: (readonly VisualCell[])[] = [];

  for (let row = 0; row < BOARD_SIZE; row += 1) {
    const sourceRow = inventory[row];
    if (sourceRow === undefined) {
      throw new RangeError(`Visual inventory row ${row} is missing.`);
    }
    const cells: VisualCell[] = [];
    for (let column = 0; column < BOARD_SIZE; column += 1) {
      const cell = sourceRow[column];
      if (cell === undefined) {
        throw new RangeError(`Visual inventory cell (${row}, ${column}) is missing.`);
      }
      cells.push(snapshotCell(cell));
    }
    rows.push(Object.freeze(cells));
  }

  return Object.freeze(rows);
}

function snapshotCell(cell: VisualCell): VisualCell {
  return Object.freeze(
    cell.kind === "gap"
      ? { kind: "gap" as const }
      : { kind: "slab" as const, discCount: cell.discCount },
  );
}

function snapshotPlan(plan: ExtractionPlan): ExtractionPlan {
  switch (plan.kind) {
    case "edge":
      return Object.freeze({ kind: "edge", direction: plan.direction });
    case "gap":
      return Object.freeze({ kind: "gap", direction: plan.direction });
    case "lift":
      return Object.freeze({ kind: "lift" });
    case "push":
      return Object.freeze({
        kind: "push",
        direction: plan.direction,
        positions: Object.freeze(plan.positions.map(snapshotPosition)),
        opening: Object.freeze(
          "kind" in plan.opening
            ? { kind: "outside" as const, ...snapshotPosition(plan.opening) }
            : snapshotPosition(plan.opening),
        ),
      });
  }
}

function snapshotPosition(position: Position): Position {
  return Object.freeze({ row: position.row, column: position.column });
}

function snapshotVector(vector: Vector): Vector {
  return Object.freeze({ row: vector.row, column: vector.column });
}

function positionKey(position: Position): string {
  return `(${position.row}, ${position.column})`;
}

function isRecord(value: unknown): value is Readonly<Record<string, unknown>> {
  return typeof value === "object" && value !== null;
}
